import { createRef } from 'react';
import { createPortal } from 'react-dom';
import { toPng } from 'html-to-image';

import JxForm from '../../../../JxControls/JxForms/JxForm.js';
import JxTitleBar from '../../../../JxControls/JxTitleBar/JxTitleBar.jsx';
import JxButton from '../../../../JxControls/JxButtons/JxButton.jsx';
import JxLineEdit from '../../../../JxControls/JxEdits/JxLineEdit.jsx';
import JxCheckbox from '../../../../JxControls/JxToggles/JxCheckbox.jsx';
import { OpenToast } from '../../../../JxControls/JxToasts/StyledToast.jsx';
import logService from '../../../../services/wss/logService3.js';
import UserSettingsContext from '../../../../contexts/UserSettingsContext.jsx';
import umlCanvasEngine from '../../../Core/UmlCanvasEngine.js';
import { computeBounds } from '../UmlWorldMapHelpers.jsx';
import { CANVAS_WORLD_SELECTOR, waitForPaint, prepareCloneForCapture } from '../UmlPreviewDlgHelpers.js';
import { computeExportFitTransform, computePreviewBoxSize, PREVIEW_MAX_WIDTH, PREVIEW_MAX_HEIGHT } from './UmlExportImageHelpers.js';
import { EXPORT_SETTINGS_KEYS, saveExportSetting, resolveExportSettings } from './UmlExportSettings.js';
import { TITLE_BAR_ACTIONS } from './UmlExportImageDlgTitleBarActions.jsx';
import './UmlExportImageDlg.css';

/**
 * UmlExportImageDlg — export the current schema as a shareable PNG, own
 * configurable size/padding/background (unlike the small, fixed-size
 * UmlPreviewDlg thumbnail). Live on-screen preview reflects settings as they
 * change; the actual capture runs at full resolution in a separate off-screen
 * host (same clone-and-html-to-image approach + off-screen-render-bug fix as
 * UmlPreviewDlg — see Jerny Source Preview note for the history).
 *
 * @param {boolean} open
 * @param {{rk: string, name: string}} note - umlCanvasEngine.loadedSchemaInfo, used for the filename
 * @param {Function} onClose
 */
export default class UmlExportImageDlg extends JxForm {
    static contextType = UserSettingsContext;

    state = {
        ...this.state,
        busy: false,
        lockAspect: true,
    };

    constructor(props) {
        super(props);
        this.name = 'UmlExportImageDlg';
        this.schema = null; // no Joi validation — plain number/color/checkbox fields
        this.previewRef = createRef();
        this.captureRef = createRef();
    }

    getDefaultGeo() {
        return { top: 80, left: 400, width: 640, height: 420 };
    }

    componentDidMount() {
        super.componentDidMount();
        this._refreshLivePreview();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
        if (this.props.open) this._refreshLivePreview();
    }

    /**
     * Clones the live canvas world into the small on-screen preview box, fit
     * to the CURRENT settings (width/height/paddingTop/background) scaled
     * down to preview-box size. Purely visual — never touches the real canvas.
     */
    _refreshLivePreview = () => {
        const container = this.previewRef.current;
        const liveWorld = document.querySelector(CANVAS_WORLD_SELECTOR);
        if (!container || !liveWorld) return;

        const { width, height, paddingTop, backgroundColor, transparent } = resolveExportSettings(this.context.settings);
        const { boxW, boxH, previewScale } = computePreviewBoxSize(width, height);
        const bounds = computeBounds(umlCanvasEngine.shapes);

        const clone = liveWorld.cloneNode(true);
        clone.style.transform = computeExportFitTransform(bounds, { width: boxW, height: boxH, paddingTop: paddingTop * previewScale });
        prepareCloneForCapture(clone);

        container.innerHTML = '';
        container.style.width = `${boxW}px`;
        container.style.height = `${boxH}px`;
        container.style.background = transparent ? '' : backgroundColor;
        container.appendChild(clone);
    };

    /**
     * Clones the live canvas world into the off-screen full-resolution
     * capture host, fit to the real export width/height/paddingTop, then
     * captures it via html-to-image.
     * @returns {Promise<string|null>} PNG data URL, or null if nothing to capture
     */
    _captureFullRes = async () => {
        const container = this.captureRef.current;
        const liveWorld = document.querySelector(CANVAS_WORLD_SELECTOR);
        if (!container || !liveWorld) return null;

        const { width, height, paddingTop, backgroundColor, transparent } = resolveExportSettings(this.context.settings);
        const bounds = computeBounds(umlCanvasEngine.shapes);

        const clone = liveWorld.cloneNode(true);
        clone.style.transform = computeExportFitTransform(bounds, { width, height, paddingTop });
        prepareCloneForCapture(clone);

        container.innerHTML = '';
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
        container.style.background = transparent ? 'transparent' : backgroundColor;
        container.appendChild(clone);

        await waitForPaint();
        return toPng(container, { skipFonts: true });
    };

    handleExportToFile = async () => {
        this.setState({ busy: true });
        try {
            const dataUrl = await this._captureFullRes();
            if (!dataUrl) {
                OpenToast('Could not generate export — see Output Log', 'error');
                return;
            }
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `${this.props.note?.name || 'schema'}.png`;
            link.click();
            OpenToast('Image exported', 'info');
        } catch (error) {
            logService.logError(`UmlExportImageDlg.handleExportToFile: ${error.message}`);
            OpenToast('Could not generate export — see Output Log', 'error');
        } finally {
            this.setState({ busy: false });
        }
    };

    handleCopyToClipboard = async () => {
        this.setState({ busy: true });
        try {
            const dataUrl = await this._captureFullRes();
            if (!dataUrl) {
                OpenToast('Could not generate export — see Output Log', 'error');
                return;
            }
            const blob = await (await fetch(dataUrl)).blob();
            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
            OpenToast('Image copied to clipboard', 'info');
        } catch (error) {
            logService.logError(`UmlExportImageDlg.handleCopyToClipboard: ${error.message}`);
            OpenToast('Could not copy to clipboard — see Output Log', 'error');
        } finally {
            this.setState({ busy: false });
        }
    };

    handleWidthChange = (value) => {
        const next = Number(value);
        if (!next || next <= 0) return; // ignore empty/invalid while typing, same as UmlCanvasToolbar.handleGridSizeChange
        const { width, height } = resolveExportSettings(this.context.settings);
        saveExportSetting(this.context, EXPORT_SETTINGS_KEYS.width, next);
        if (this.state.lockAspect && width > 0) {
            saveExportSetting(this.context, EXPORT_SETTINGS_KEYS.height, Math.round((next * height) / width));
        }
    };

    handleHeightChange = (value) => {
        const next = Number(value);
        if (!next || next <= 0) return;
        const { width, height } = resolveExportSettings(this.context.settings);
        saveExportSetting(this.context, EXPORT_SETTINGS_KEYS.height, next);
        if (this.state.lockAspect && height > 0) {
            saveExportSetting(this.context, EXPORT_SETTINGS_KEYS.width, Math.round((next * width) / height));
        }
    };

    handleLockAspectChange = (checked) => {
        this.setState({ lockAspect: checked });
    };

    handlePaddingTopChange = (value) => {
        const next = Number(value);
        if (Number.isNaN(next) || next < 0) return; // 0 is a valid value, unlike width/height above
        saveExportSetting(this.context, EXPORT_SETTINGS_KEYS.paddingTop, next);
    };

    handleBackgroundColorChange = (e) => {
        saveExportSetting(this.context, EXPORT_SETTINGS_KEYS.backgroundColor, e.target.value);
    };

    handleTransparentChange = (checked) => {
        saveExportSetting(this.context, EXPORT_SETTINGS_KEYS.transparent, checked);
    };

    render() {
        if (!this.props.open) return null;

        const { width, height, paddingTop, backgroundColor, transparent } = resolveExportSettings(this.context.settings);
        const { boxW, boxH } = computePreviewBoxSize(width, height);

        const panel = (
            <div
                {...this.panelProps}
                style={this.panelStyle}
                className={`jx-panel ueid-panel ${this.panelActiveClass}`}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Export Image"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={TITLE_BAR_ACTIONS(this.props.onClose)}
                />

                <div className="ueid-body">
                    <div className="ueid-preview-frame" style={{ width: PREVIEW_MAX_WIDTH, height: PREVIEW_MAX_HEIGHT }}>
                        <div
                            className={`ueid-preview-box${transparent ? ' ueid-preview-checkerboard' : ''}`}
                            style={{ width: boxW, height: boxH }}
                            ref={this.previewRef}
                        />
                    </div>

                    <div className="ueid-settings">
                        <label className="ueid-field">
                            <span>Width</span>
                            <JxLineEdit id="ueid_width" value={String(width)} onChange={this.handleWidthChange} />
                        </label>
                        <label className="ueid-field">
                            <span>Height</span>
                            <JxLineEdit id="ueid_height" value={String(height)} onChange={this.handleHeightChange} />
                        </label>
                        <JxCheckbox
                            id="ueid_lockAspect"
                            checked={this.state.lockAspect}
                            onChange={this.handleLockAspectChange}
                            label="Lock aspect ratio"
                        />
                        <label className="ueid-field">
                            <span>Padding Top</span>
                            <JxLineEdit id="ueid_paddingTop" value={String(paddingTop)} onChange={this.handlePaddingTopChange} />
                        </label>
                        <label className="ueid-field">
                            <span>Background</span>
                            <input
                                id="ueid_backgroundColor"
                                className="ueid-color-input"
                                type="color"
                                value={backgroundColor}
                                onChange={this.handleBackgroundColorChange}
                                disabled={transparent}
                            />
                        </label>
                        <JxCheckbox
                            id="ueid_transparent"
                            checked={transparent}
                            onChange={this.handleTransparentChange}
                            label="Transparent background"
                        />
                    </div>
                </div>

                <div className="ueid-footer">
                    <JxButton
                        className="jx-button-sm"
                        label="Copy to Clipboard"
                        onClick={this.handleCopyToClipboard}
                        disabled={this.state.busy}
                    />
                    <JxButton
                        className="jx-button-sm"
                        label="Export to File"
                        onClick={this.handleExportToFile}
                        disabled={this.state.busy}
                    />
                </div>

                {/* off-screen full-resolution capture host — same fixed(-9999px) approach as UmlPreviewDlg.css,
                    a real React child (portaled) rather than a hand-built detached node, per the
                    html-to-image off-screen-render-bug fix (see Jerny Source - Preview note) */}
                <div className="ueid-offscreen-host">
                    <div className="ueid-capture-frame" ref={this.captureRef} />
                </div>
            </div>
        );

        return createPortal(panel, document.body);
    }
}
